import { QueryDocumentSnapshot } from "@firebase/firestore-types";
import { Action } from "redux";
import { CollectionState, DocState } from "src/store";

export type CollectionListenAction<T> = Action & { payload: { page?: number, startAfter?: QueryDocumentSnapshot } };
export type CollectionChangeAction<T> = Action & { payload: { page?: number, docs: T[], last?: QueryDocumentSnapshot } };

export type DocListenAction = Action & { payload: { id: string } };
export type DocChangeAction<T> = Action & { payload: { doc: T } };

export function createRedux<T>(name: string) {
  const actionTypes = {
    LISTEN_COLLECTION: `${name}/LISTEN_COLLECTION`,
    CHANGE_COLLECTION: `${name}/CHANGE_COLLECTION`,
    UNLISTEN_COLLECTION: `${name}/UNLISTEN_COLLECTION`,

    LISTEN_DOC: `${name}/LISTEN_DOC`,
    CHANGE_DOC: `${name}/CHANGE_DOC`,
    UNLISTEN_DOC: `${name}/UNLISTEN_DOC`,
  }

  const actions = {
    listenCollection: (page?: number, startAfter?: QueryDocumentSnapshot) => {
      return {
        type: actionTypes.LISTEN_COLLECTION,
        payload: { page, startAfter }
      };
    },
    changeCollection: (data: {
      page?: number,
      docs: T[],
      last?: QueryDocumentSnapshot,
    }) => {
      return {
        type: actionTypes.CHANGE_COLLECTION,
        payload: data,
      } as CollectionChangeAction<T>;
    },
    unlistenCollection: () => {
      return {
        type: actionTypes.UNLISTEN_COLLECTION,
        payload: {},
      };
    },
    listenDoc: (id: string) => {
      return {
        type: actionTypes.LISTEN_DOC,
        payload: { id }
      };
    },
    changeDoc: (doc: T) => {
      return {
        type: actionTypes.CHANGE_DOC,
        payload: { doc }
      };
    },
    unlistenDoc: () => {
      return {
        type: actionTypes.UNLISTEN_DOC,
      };
    }
  };

  const collectionReducer = (
    state: CollectionState<T> = { status: 'none' },
    action: CollectionListenAction<T> | CollectionChangeAction<T>) => {

    switch (action.type) {
      case actionTypes.LISTEN_COLLECTION: {
        const { payload: { page, startAfter } } = action as CollectionListenAction<T>;
        const pagination = state.status !== 'none' ?
          state.pagination :
          { [page || 0]: { startAfter } };

        return {
          status: 'loading',
          data: [],
          currentPage: page,
          pagination,
        };
      }
      case actionTypes.CHANGE_COLLECTION: {
        const { payload: { page, docs, last } } = action as CollectionChangeAction<T>;
        const pagination = state.status !== 'none' ? state.pagination : {};
        pagination[page ? page + 1 : 0] = { startAfter: last };

        return {
          status: 'loaded',
          data: docs,
          currentPage: page,
          pagination,
        };
      }
      case actionTypes.UNLISTEN_COLLECTION: {
        return {
          status: 'none'
        };
      }
      default: {
        return state;
      }
    }
  };

  const docReducer = (
    state: DocState<T> = { status: 'none' },
    action: DocListenAction | DocChangeAction<T>) => {

    switch (action.type) {
      case actionTypes.LISTEN_DOC: {
        return {
          status: 'loading',
        };
      }
      case actionTypes.CHANGE_DOC: {
        const { payload: { doc } } = action as DocChangeAction<T>;
        return {
          status: 'loaded',
          data: doc,
        };
      }
      case actionTypes.UNLISTEN_DOC: {
        return {
          status: 'none',
        };
      }
      default:
        return state;
    }
  };

  return {
    actionTypes,
    actions,
    collectionReducer,
    docReducer,
  };
}



// WEBPACK FOOTER //
// ./src/firebase/redux.ts